import {
    clone, IControl, IOutput, IAction, IOutputValue, EOutputLookupContext, ISharedJourney, IComponentDictionary, EComponentTypes, IControlBlock, PropertyHelper,
    EOutputScope, ILoop, IOutputUsed
} from '../../helpers';
import { DEFAULT_CONTENT_EDITABLE_VALUE } from '../../constants';
import types from '../types';
import store from '../store';

interface IOutputState {
    outputs: IOutputValue[];
    globalOutputs: IOutput[];
    outputSearchElementId: string;
    outputSearchTerm: string;
    outputSearchTop: number;
    previousOutputBlocks: any;
    outputLookupContext: EOutputLookupContext;
    focusedOutputKey: string;
    outputLookupRequestIndexForOutputId: string;
    outputLookupIndex: string;
    outputsInUse: IOutputUsed[];
}

const initialState: IOutputState = {
    outputs: [],
    globalOutputs: [],
    outputLookupContext: EOutputLookupContext.NotSpecified,
    outputSearchElementId: '',
    outputSearchTerm: '',
    outputSearchTop: -999999,
    previousOutputBlocks: {},
    focusedOutputKey: '',
    outputLookupRequestIndexForOutputId: '',
    outputLookupIndex: null,
    outputsInUse: null

};

const outputReducer = function (state: IOutputState = initialState, action) {

    let stateClone: IOutputState = clone(state);

    switch (action.type) {

        case types.UPDATE_CONTROL:
            stateClone = updateControlOutputs(stateClone, action.componentId, action.control, action.parentLoopId);
            break;

        case types.DELETE_ACTION_OUTPUTS:
            stateClone = deleteOutputs(stateClone, action.outputs);
            break;

        case types.DELETE_CONTROL:
            stateClone = deleteControlOutputs(stateClone, action.outputs);
            break;
            
        case types.DELETE_LAYOUT_PANEL:
            stateClone = deleteControlOutputs(stateClone, action.outputs);
            break;

        case types.UPDATE_ACTION:
            stateClone = updateOutputs(stateClone, action.componentId, action.action, action.parentLoopId, action.deletedOutputs);
            break;

        case types.UPDATE_ACTION_IN_OUTPUT:
            stateClone = updateOutputs(stateClone, action.componentId, action.action, action.parentLoopId, action.deletedOutputs);
            break;

        case types.UPDATE_LOOP:
            stateClone = updateLoopOutputs(stateClone, action.componentId, action.outputValues);
            break;

        case types.DELETE_LOOP:
            stateClone = deleteLoopOutputs(stateClone, action.componentId);
            break;

        case types.ADD_SHARED_JOURNEY_OUTPUTS:
            stateClone = addSharedJourneyOutputs(stateClone, action.componentId, action.outputs);
            break;

        case types.DELETE_COMPONENT:
            stateClone = deleteComponentOutputs(stateClone, action.componentId);
            break;

        case types.DELETE_COMPONENT_OUTPUTS:
            stateClone = deleteComponentOutputs(stateClone, action.componentId);
            break;

        case types.SET_OUTPUT_STATE:
            stateClone = setState(stateClone, action.outputs, action.globalOutputs, action.outputsInUse);
            break;

        case types.UPDATE_OUTPUTS_IN_USE:
            stateClone = updateOutputsInUse(stateClone, action.outputsInUse);
            break;

        case types.UPDATE_GLOBAL_OUTPUTS:
            stateClone = updateGlobalOutputs(stateClone, action.outputs);
            break;

        case types.OPEN_OUTPUT_LOOKUP:
            stateClone = openOutputLookup(stateClone, action.elementId, action.context);
            break;

        case types.CLOSE_OUTPUT_LOOKUP:
            stateClone = closeOutputLookup(stateClone);
            break;

        case types.SET_OUTPUT_SEARCH:
            stateClone = setOutputSearchTerm(stateClone, action.searchElementId, action.searchTerm, action.offsetTop, action.outputLookupContext);
            break;

        case types.CLEAR_OUTPUT_SEARCH:
            stateClone = clearOutputSearch(stateClone);
            break;

        case types.SET_PREVIOUS_OUTPUT_BLOCKS:
            stateClone = setPreviousOutputBlocks(stateClone, action.previousOutputBlocks);
            break;

        case types.UPDATE_SHARED_JOURNEY:
            stateClone = updateOutputs(stateClone, action.componentId, action.sharedJourney);
            break;

        case types.DELETE_COMPONENT_CHAIN:
            stateClone = deleteComponentChainOutputs(stateClone, action.componentId, action.components);
            break;

        case types.SET_FOCUSED_OUTPUT_KEY:
            stateClone = setFocusedOutputKey(stateClone, action.focusedOutputKey);
            break;

        case types.UPDATE_CONTROL_BLOCK:
            stateClone = handleControlBlockOutputs(stateClone, action.controlBlock);
            break;

        case types.UPDATE_LOOP_OUTPUT:
            stateClone = updateLoopOutput(stateClone, action.output);
            break;

        case types.SET_INDEX_REQUEST_OUTPUT_ID:
            stateClone = setIndexRequestOutputId(stateClone, action.outputLookupRequestIndexForOutputId)
            break;

        case types.UPDATE_OUTPUT_LOOKUP_REQUEST_INDEX:
            stateClone = updateOutputLookupRequestIndex(stateClone, action.outputLookupIndex);
            break;

        default:
            break;

    }

    return stateClone;

};


let updateOutputs = (state: IOutputState, componentId: string, component: IControl | IAction | ISharedJourney, parentLoopId?: string, deletedOutputs?: string[]): IOutputState => {

    let outputs = component.OutputValues;

    let outputCollection = state.outputs;

    if (outputs) {

        const outputLength = outputCollection.length;
        outputCollection.slice().reverse().forEach((output, i) => {
            if (output.componentId === componentId) {
                outputCollection.splice(outputLength - i - 1, 1);
            }
        });

        outputs.forEach((output, i) => {

            let o = outputCollection.filter(o => o.Id === output.Id)[0];
            let outputValue = {
                ...output,
                componentId: componentId,
                LoopId: parentLoopId,
                OutputScope: parentLoopId ? EOutputScope.LoopLocal : EOutputScope.Local
            };

            if (!o) {
                outputCollection.push(outputValue);
            }
            else {
                o.Name = output.Name;
                o.Group = output.Group;
                o.DisplayOnSummary = output.DisplayOnSummary;
                o.Order = output.Order;
                o.GlobalOutputId = output.GlobalOutputId;
            }

        });

    }

    if (deletedOutputs) {
        deletedOutputs.forEach(x => {
            state.outputsInUse = removeAllFromLists(state.outputsInUse, x);
        });
    }

    function removeAllFromLists(dictionary, element) {
        for (const key in dictionary) {
            if (dictionary.hasOwnProperty(key) && Array.isArray(dictionary[key])) {
                let index = dictionary[key].indexOf(element);
                while (index !== -1) {
                    dictionary[key].splice(index, 1);
                    index = dictionary[key].indexOf(element);
                }
            }
        }
        return dictionary;
    }

    return state;

};

let updateControlOutputs = (state: IOutputState, componentId: string, component: IControl | IAction | ISharedJourney, parentLoopId?: string): IOutputState => {

    let outputs = component.OutputValues;

    let outputCollection = state.outputs;

    if (outputs) {

        outputs.forEach((output, i) => {

            let o = outputCollection.filter(o => o.Id === output.Id)[0];
            let outputValue = {
                ...output,
                componentId: componentId,
                LoopId: parentLoopId,
                OutputScope: parentLoopId ? EOutputScope.LoopLocal : EOutputScope.Local
            };

            if (!o) {
                outputCollection.push(outputValue);
            }
            else {
                o.Name = output.Name;
                o.Group = output.Group;
                o.DisplayOnSummary = output.DisplayOnSummary;
                o.Order = output.Order;
                o.GlobalOutputId = output.GlobalOutputId;
            }

        });

    }

    return state;


};

let updateLoopOutputs = (state: IOutputState, componentId: string, outputValues: IOutputValue[]): IOutputState => {

    state.outputs = state.outputs.filter(o => o.componentId !== componentId);

    state.outputs = state.outputs.concat(outputValues);

    return state;

};

let updateLoopOutput = (state: IOutputState, output: IOutputValue): IOutputState => {

    state.outputs = state.outputs.map(o => {

        if (o.Id === output.Id && o.componentId === output.componentId) {
            return output;
        }

        return o;

    });

    return state;

};

let deleteLoopOutputs = (state: IOutputState, componentId: string) => {

    state.outputs = state.outputs.filter(o => o.componentId !== componentId);

    return state;

};

let addSharedJourneyOutputs = (state: IOutputState, componentId: string, outputs: IOutputValue[]): IOutputState => {

    let outputsToAdd = outputs.map((o) => {
        o.componentId = componentId;
        o.Name = o.LogicalName;
        return o;
    });

    state.outputs = state.outputs.concat(outputsToAdd);

    return state;

};

let deleteOutputs = (state: IOutputState, outputs: IOutput[]): IOutputState => {

    if (outputs) {
        let ids: string[] = outputs.map(output => output.Name);
        let componentIds: string[] = outputs.map(output => output.componentId);
        state.outputs = state.outputs
            .filter(output => {
                const result = (ids.indexOf(output.Name) < 0) || (componentIds.indexOf(output.componentId) < 0);
                return result;
            });
    }

    return state;

};

let deleteControlOutputs = (state: IOutputState, outputs: IOutput[]): IOutputState => {

    if (outputs) {
        let ids: string[] = outputs.map(output => output.Id);
        state.outputs = state.outputs
            .filter(output => ids.indexOf(output.Id) < 0);
    }

    return state;

};

let deleteComponentOutputs = (state: IOutputState, componentId: string): IOutputState => {

    state.outputs = state.outputs.filter(o => o.componentId !== componentId);
    return state;

};

let setState = (state: IOutputState, outputs: any[], globalOutputs: IOutput[], outputsInUse: IOutputUsed[]): IOutputState => {

    state.outputs = outputs.map(o => {
        return { ...o, componentId: o.componentId };
    });

    state.globalOutputs = globalOutputs;
    state.outputsInUse = outputsInUse;

    return state;

};

let updateOutputsInUse = (state: IOutputState, outputsInUse: IOutputUsed[]): IOutputState => {

    state.outputsInUse = outputsInUse;

    return state;

};

const updateGlobalOutputs = (state: IOutputState, outputs: IOutput[]): IOutputState => {

    state.globalOutputs = outputs;

    return state;
};

let openOutputLookup = (state: IOutputState, elementId: string, context: EOutputLookupContext) => {

    state.outputSearchElementId = elementId;
    state.outputLookupContext = context;
    state.outputLookupRequestIndexForOutputId = null;
    state.outputLookupIndex = '';

    return state;

};

let closeOutputLookup = (state: IOutputState) => {

    state.outputSearchElementId = '';
    state.outputLookupContext = EOutputLookupContext.NotSpecified;
    state.focusedOutputKey = '';
    state.outputLookupRequestIndexForOutputId = null;
    state.outputLookupIndex = '';

    return state;

};

let setOutputSearchTerm = (state: IOutputState, searchElementId: string, searchTerm: string, offsetTop: number, outputLookupContext: EOutputLookupContext) => {

    //remove all zero width spaces from the search term
    searchTerm = searchTerm.replace(/[\u200B-\u200D\uFEFF]/g, '');

    state.outputSearchElementId = searchElementId;
    state.outputSearchTerm = searchTerm;
    state.outputSearchTop = offsetTop;
    state.outputLookupContext = outputLookupContext;

    return state;

};

let clearOutputSearch = (state: IOutputState) => {

    state.outputSearchElementId = initialState.outputSearchElementId;
    state.outputSearchTerm = initialState.outputSearchTerm;
    state.outputSearchTop = initialState.outputSearchTop;
    state.focusedOutputKey = '';
    state.outputLookupRequestIndexForOutputId = null;
    state.outputLookupIndex = '';

    return state;

};

let setPreviousOutputBlocks = (state: IOutputState, previousOutputBlocks: any) => {

    state.previousOutputBlocks = previousOutputBlocks;

    return state;

};

let deleteComponentChainOutputs = (state: IOutputState, componentId: string, components: IComponentDictionary) => {
    //delete the outputs that belong to the components that are in the chain starting from componentId
    while (componentId) {
        let component = components[componentId];

        if (component.ComponentType.ComponentTypeId === EComponentTypes.decision) {
            for (let i = 0; i < component.Decision.Branches.length; i++) {
                if (component.Decision.Branches[i].NextComponent && component.Decision.Branches[i].NextComponent.ComponentId) {
                    state = deleteComponentChainOutputs(state, component.Decision.Branches[i].NextComponent.ComponentId, components);
                }
            }
        }

        state.outputs = state.outputs.filter(o => o.componentId !== componentId);
        componentId = component.NextComponent ? component.NextComponent.ComponentId : null;
    }
    return state;
};

let setFocusedOutputKey = (state: IOutputState, focusedOutputKey: string) => {

    state.focusedOutputKey = focusedOutputKey;

    return state;
};

let handleControlBlockOutputs = (state: IOutputState, controlBlock: IControlBlock) => {

    if (PropertyHelper.getPropertyValue(controlBlock.Properties, 'Is Repeatable') === true) {
        controlBlock.Controls.forEach(control => {

            if (control.OutputValues) {
                control.OutputValues.forEach(outputValue => {

                    let output = state.outputs.filter(o => o.Id === outputValue.Id)[0];

                    if (output) {
                        output.Group = '';
                    }

                });
            }

        });
    }

    return state;
};

let setIndexRequestOutputId = (state: IOutputState, outputLookupRequestIndexForOutputId: string) => {

    state.outputLookupRequestIndexForOutputId = outputLookupRequestIndexForOutputId;
    state.outputLookupIndex = initialState.outputLookupIndex;

    return state;

};

let updateOutputLookupRequestIndex = (state: IOutputState, outputLookupIndex: string) => {

    state.outputLookupIndex = outputLookupIndex;

    return state;

};

export default outputReducer;


// WEBPACK FOOTER //
// ./source/ts/store/reducers/outputs.ts